import React, { useContext } from 'react';
import { ChevronDown, Filter } from 'lucide-react';
import { AuthContext } from 'components/context/AuthContext';
import { useAdsModal } from 'components/context/ModalsContext';
import { useGoogleAds } from 'hooks/useGoogleAds';
import { useMetaAds } from 'hooks/useMetaAds';
import { GoogleIcon, MetaIcon } from 'components';
import { useMobileFilter } from './context/MobileFilterContext';
import MobileBrandSelectorSheet from './BrandSelector/MobileBrandSelectorSheet';
import CompactConnectionBadge from './BrandSelector/CompactConnectionBadge';

/**
 * MobileTopNavBar
 * - Shows only on mobile/tablet (block lg:hidden)
 * - Displays the logo with words centered
 * - Accepts children as a slot for right-side content (e.g., a button), absolutely positioned
 * - Shows a back button on the left. If onBack is provided, calls onBack. Otherwise, disabled.
 * - Shows brand selector button on the right for mobile users
 */
const MobileTopNavBar = ({
  children,
  sticky = false,
  transparent = false,
  hideBrandSelector = false,
  hideFilters = false,
  filterAdjacentContent = null,
}) => {
  const { auth, hasGoogleAdsConnected, hasMetaConnected } = useContext(AuthContext);
  const { openConnectionsModal } = useAdsModal();
  const { connectGoogleAds } = useGoogleAds();
  const { startMetaOAuth } = useMetaAds();
  const { isMobileFilterOpen, toggleMobileFilter } = useMobileFilter();
  const [isBrandSelectorOpen, setIsBrandSelectorOpen] = React.useState(false);

  const activeBrand = auth.user?.brands?.find(b => b.id === auth.user?.active_brand_id);
  const showBrandSelector = !hideBrandSelector && activeBrand;
  const showFilterIcon = !hideFilters && showBrandSelector;
  const googleConnected = activeBrand ? Boolean(activeBrand.google_ads_connected) : hasGoogleAdsConnected;
  const metaConnected = activeBrand ? Boolean(activeBrand.meta_connected) : hasMetaConnected;

  return (
    <>
      <div
        className={`block lg:hidden w-full ${transparent ? 'bg-transparent' : 'bg-transparent'} px-3 py-2 pt-4 flex items-center justify-center ${sticky ? 'sticky top-10 z-50' : 'fixed top-10 left-0 right-0 z-[9999]'}`}
      >

        {/* Right side content - Filter icon, connection badges, and brand selector */}
        <div className="absolute right-6 top-1/2 -translate-y-1/2 flex items-center gap-1.5">
          {filterAdjacentContent ? (
            <div className="flex items-center shrink-0">{filterAdjacentContent}</div>
          ) : null}

          {/* Filter icon — a la derecha del slot opcional (p. ej. Crear campaña) */}
          {showFilterIcon && (
            <button
              type="button"
              onClick={toggleMobileFilter}
              className={`p-2 rounded-lg transition-all active:scale-95 touch-manipulation border ${
                isMobileFilterOpen
                  ? 'bg-white/20 border-white/30'
                  : 'bg-white/10 hover:bg-white/15 border-white/20'
              }`}
              title={isMobileFilterOpen ? 'Cerrar filtros' : 'Abrir filtros'}
              aria-label={isMobileFilterOpen ? 'Cerrar filtros' : 'Abrir filtros'}
            >
              <Filter size={18} className="text-white/70" />
            </button>
          )}

          {/* Connection status badges */}
          {showBrandSelector && (
            <>
              <CompactConnectionBadge
                connected={googleConnected}
                icon={GoogleIcon}
                label="Google Ads"
                onClick={googleConnected ? openConnectionsModal : () => connectGoogleAds(false)}
              />
              <CompactConnectionBadge
                connected={metaConnected}
                icon={MetaIcon}
                label="Meta Ads"
                onClick={metaConnected ? openConnectionsModal : () => startMetaOAuth(false)}
              />
            </>
          )}

          {/* Children (typically filter icon from filter bars) */}
          {children && (
            <div className="flex items-center gap-1.5">
              {children}
            </div>
          )}

          {/* Brand selector button */}
          {showBrandSelector && (
            <button
              type="button"
              onClick={() => setIsBrandSelectorOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/10 border border-white/20 hover:bg-white/15 text-white text-xs font-medium transition-all active:scale-95 touch-manipulation"
              title="Cambiar marca"
            >
              <span className="truncate max-w-[70px]">{activeBrand?.name || 'Marca'}</span>
              <ChevronDown size={14} className="flex-shrink-0 text-white/60" />
            </button>
          )}
        </div>
      </div>

      {/* Mobile brand selector sheet */}
      {showBrandSelector && (
        <MobileBrandSelectorSheet
          isOpen={isBrandSelectorOpen}
          onClose={() => setIsBrandSelectorOpen(false)}
        />
      )}
    </>
  );
};

export default MobileTopNavBar; 